import { useCallback, useState } from 'react'
import { AccountModule } from '@core/modules/account/account.module'
import { TDomain } from '@core/modules/domains/domains'
import { DomainsModule } from '@core/modules/domains/domains.module'
import { UserInputsModule } from '@core/modules/user-inputs'
import { UserInputService } from '@core/services/api-gateway'
import { formatDate } from '@core/utils/time'
import { Domain } from '@routes/settings/routes/account/domain'
import ContentItem from '@components/page-content/content-item'
import { generateToast } from '@components/toasting'
import Typography from '@/ui/components/typography'
import { TTheme, useCustomTheme } from '@/core/theme'
import { Card } from '@routes/settings/routes/account/settings.account.route.styles'
import {
    AccountDetailsGrid,
    AccountFields,
    CardTitle,
    DomainsColumn,
} from './accountDetails.styles'

const AccountDetails = () => {
    const theme = useCustomTheme() as TTheme
    const [domainsInProcess, setDomainsInProcess] = useState<string[]>([])
    const { currentAccount, nextScanDate } = AccountModule.useStore()
    const domains = DomainsModule.getAcceptedDomainsWithSubscription()

    const handleOnDeleteDomainClick = useCallback(async (id: string) => {
        setDomainsInProcess((prev) => [...prev, id])
        const result = await UserInputService.deleteDomainUserInput(id)
        if (!result) {
            generateToast({
                type: 'error',
                data: 'An error was occurred, please try again later',
            })
            DomainsModule.updateDeletedDomainsAsInProcess([])
            setDomainsInProcess([])
        } else {
            UserInputsModule.pollProcesses()
        }
    }, [])

    return (
        <Card>
            <CardTitle>
                <Typography
                    component="p"
                    variant="m-regular"
                    color={theme.colors.greys[800]}
                >
                    Account details
                </Typography>
            </CardTitle>
            <AccountDetailsGrid>
                <AccountFields>
                    <ContentItem label="Account name" className="account-name">
                        <Typography
                            variant="s-regular"
                            color={theme.colors.greys[800]}
                        >
                            {currentAccount?.company_name}
                        </Typography>
                    </ContentItem>
                    <ContentItem label="Date account created">
                        <Typography
                            variant="s-regular"
                            color={theme.colors.greys[800]}
                        >
                            {currentAccount?.date_created
                                ? formatDate(currentAccount.date_created)
                                : '---'}
                        </Typography>
                    </ContentItem>
                    <ContentItem label="Next scan date">
                        <Typography
                            variant="s-regular"
                            color={theme.colors.greys[800]}
                        >
                            {nextScanDate ? formatDate(nextScanDate) : '---'}
                        </Typography>
                    </ContentItem>
                    <ContentItem label="Industry">
                        <Typography
                            variant="s-regular"
                            color={theme.colors.greys[800]}
                        >
                            {currentAccount?.industry ?? '---'}
                        </Typography>
                    </ContentItem>
                </AccountFields>
                <DomainsColumn>
                    <ContentItem label="Domains">
                        {domains.length ? (
                            domains?.map((domain: TDomain) => (
                                <Domain
                                    key={domain.id}
                                    domain={domain}
                                    domainsInProcess={domainsInProcess}
                                    handleOnDeleteDomainClick={
                                        handleOnDeleteDomainClick
                                    }
                                />
                            ))
                        ) : (
                            <Typography
                                variant="s-regular"
                                color={theme.colors.greys[500]}
                            >
                                No Domain found, please add a domain
                            </Typography>
                        )}
                    </ContentItem>
                </DomainsColumn>
            </AccountDetailsGrid>
        </Card>
    )
}

export default AccountDetails
